/**
 * CollectorFreshnessBanner.js
 * ---------------------------
 * Drop-in banner for any collector-backed page. Hits `/api/collectors/freshness/{key}`
 * and renders one of three states:
 *   - fresh:       (nothing — no banner)
 *   - stale:       ⚠ Data last refreshed Xh ago — may be stale.   [Refresh now]
 *   - never_run:   ℹ Collector not yet run — click Seed Defaults  [Seed Defaults]
 *
 * Usage:
 *   <CollectorFreshnessBanner collectorKey="rbi_defaulters" />
 *   <CollectorFreshnessBanner collectorKey="watchlist" />
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin;

function ageLabel(hours) {
  if (hours == null) return "never";
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  if (hours < 48) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function CollectorFreshnessBanner({ collectorKey }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/collectors/freshness/${collectorKey}`, {
        withCredentials: true,
      });
      setData(data);
    } catch {
      // Silent — banner is a hint, not a blocker
    }
  }, [collectorKey]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    if (!data?.seed_endpoint) {
      toast.error("This collector has no automated seed endpoint.");
      return;
    }
    setRefreshing(true);
    try {
      await axios.post(`${API}${data.seed_endpoint}`, {}, { withCredentials: true });
      toast.success(`${data.label}: refresh triggered`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed (admin only)");
    } finally {
      setRefreshing(false);
    }
  };

  if (!data || data.state === "fresh" || data.state === "error") return null;

  const isNeverRun = data.state === "never_run";
  const Icon = isNeverRun ? Info : AlertTriangle;
  const color = isNeverRun ? "#60a5fa" : "#f59e0b";
  const bg = isNeverRun ? "rgba(96,165,250,0.06)" : "rgba(245,158,11,0.06)";
  const border = isNeverRun ? "#60a5fa55" : "#f59e0b55";
  const message = isNeverRun
    ? `${data.label}: collector not yet run — no data available yet.`
    : `${data.label}: data last refreshed ${ageLabel(data.age_hours)} (SLA: every ${data.sla_hours}h) — may be stale.`;
  const ctaLabel = isNeverRun ? "Seed Defaults" : "Refresh now";

  return (
    <div
      data-testid={`collector-banner-${collectorKey}`}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        background: bg, border: `1px solid ${border}`,
        borderRadius: 8, padding: "10px 14px", marginBottom: 14, color,
      }}
    >
      <Icon size={15} />
      <span style={{ flex: 1, fontSize: 12, color: "#cbd5e1" }}>{message}</span>
      {data.seed_endpoint && (
        <button
          onClick={seed}
          disabled={refreshing}
          data-testid={`collector-banner-cta-${collectorKey}`}
          style={{
            background: "transparent", border: `1px solid ${border}`,
            color, borderRadius: 6, padding: "5px 12px",
            fontSize: 11, fontWeight: 700, cursor: refreshing ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          <RefreshCw size={11} style={{ animation: refreshing ? "spin 1s linear infinite" : "none" }} />
          {refreshing ? "Working…" : ctaLabel}
        </button>
      )}
    </div>
  );
}

export default CollectorFreshnessBanner;
